import React, { ReactElement, useContext, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { AppContext } from "AppContext";

import { isNil } from "ramda";

import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Unstable_Grid2";
import useMediaQuery from "@mui/material/useMediaQuery";

import IconSelector from "components/shared/images/IconSelector";

import {
 getAllClusterNodes,
 getAllClusterServices,
 getClusterDbServer,
 getClusterNfsServer,
 postCheckDbInstalled,
 postCheckNfsInstalled
} from "redux/handlers/clusterHandler";
import { postServerDetails } from "redux/handlers/serverHandler";

import { getClusterDb, getClusterList, getClusterNfs } from "redux/selectors/clusterSelector";

import { useAppDispatch } from "hooks/reduxHook";

import { IClusterServiceData } from "types/api/clusterApiInterface";

import "../../theme/style/connector.css";

import AddDbServerToCluster from "./actions/AddDbServerToCluster";
import AddServerToCluster from "./actions/AddServerToCluster";
import ConnectNfsServer from "./actions/ConnectNfsServer";
import JoinCluster from "./actions/JoinCluster";
import ClusterProxy from "./clusterComponents/ClusterProxy";
import ClusterServer from "./clusterComponents/ClusterServer";
import ClusterServerAppServices from "./clusterComponents/ClusterServerAppServices";
import ClusterServerReplicas from "./clusterComponents/ClusterServerReplicas";
import ClusterServerStatistics from "./clusterComponents/ClusterServerStatistics";
import DbServer from "./clusterComponents/DbServer";
import MainServer from "./clusterComponents/MainServer";
import NfsServer from "./clusterComponents/NfsServer";

const ClusterManage = (): ReactElement => {
 const params = useParams<{ id: string }>();
 const dispatch = useAppDispatch();
 const theme = useTheme();
 const desktopViewPort = useMediaQuery(theme.breakpoints.up("md"));

 const { socket } = useContext(AppContext);

 const clusterList = useSelector(getClusterList);
 const nfsData = useSelector(getClusterNfs);
 const dbData = useSelector(getClusterDb);

 const [reloadData, setReloadData] = useState<boolean>(false);
 const [reloadReplicas, setReloadReplicas] = useState<boolean>(true);
 const [serviceList, setServiceList] = useState<Array<IClusterServiceData>>([]);
 const [nfsInstalled, setNfsInstalled] = useState<boolean>(false);
 const [dbInstalled, setDbInstalled] = useState<boolean>(false);

 useEffect(() => {
  function onClusterListUpdated(value: any) {
   if (value?.completed) {
    setReloadData(true);
   }
  }
  !isNil(socket) && socket.on("cluster_data", onClusterListUpdated);
  return () => {
   !isNil(socket) && socket.off("cluster_data", onClusterListUpdated);
  };
 }, [socket]);

 useEffect(() => {
  (async () => {
   setServiceList(await dispatch(getAllClusterServices(Number(params.id))));
   const nfsinst = await dispatch(postCheckNfsInstalled(Number(params.id)));
   setNfsInstalled(nfsinst);
   if (nfsinst) await dispatch(getClusterNfsServer(Number(params.id)));
   const dbinst = await dispatch(postCheckDbInstalled(Number(params.id)));
   setDbInstalled(dbinst);
   if (dbinst) await dispatch(getClusterDbServer(Number(params.id)));
  })();
 }, []);

 useEffect(() => {
  if (reloadData) {
   (async () => {
    setServiceList(await dispatch(getAllClusterServices(Number(params.id))));
    const nfsinst = await dispatch(postCheckNfsInstalled(Number(params.id)));
    setNfsInstalled(nfsinst);
    if (nfsinst) await dispatch(getClusterNfsServer(Number(params.id)));
    const dbinst = await dispatch(postCheckDbInstalled(Number(params.id)));
    setDbInstalled(dbinst);
    if (dbinst) await dispatch(getClusterDbServer(Number(params.id)));
    await dispatch(getAllClusterNodes(Number(params.id), ""));
    await dispatch(postServerDetails(Number(params.id)));
    setReloadData(false);
   })();
  }
 }, [reloadData]);

 return (
  <Grid container spacing={2} sx={{ mt: 2 }}>
   <Grid xs={12}>
    <Stack direction="row" mb={2} alignItems="center" spacing={2}>
     <IconSelector icon="ClusterIcon" props={{ color: "kxOrange", sx: { fontSize: 80 } }} />
     <Stack textAlign="left">
      <Typography variant="unboundedTitle" fontSize={20} textTransform="uppercase">
       <FormattedMessage id="cluster.title" />
      </Typography>
      <Typography variant="subtitle2">
       <FormattedMessage id="cluster.explaining" />
      </Typography>
     </Stack>
    </Stack>
   </Grid>
   <Grid xs={desktopViewPort ? 4 : 12}>
    <Stack>
     <Stack>
      <MainServer nfsInstalled={nfsInstalled} />
     </Stack>
     <div className="verticalConnector" />
     {nfsInstalled && nfsData ? (
      <Stack spacing={2}>
       <NfsServer serverData={nfsData} />
      </Stack>
     ) : (
      <Stack spacing={2}>
       <ConnectNfsServer server_id={Number(params.id)} />
      </Stack>
     )}
     <div className="verticalConnector" />
     {dbInstalled && dbData ? (
      <Stack spacing={2}>
       <DbServer serverData={dbData} />
      </Stack>
     ) : (
      <Stack spacing={2}>
       <AddDbServerToCluster server_id={Number(params.id)} />
      </Stack>
     )}
     <div className="verticalConnector" />
     <Stack spacing={2}>
      {clusterList?.length === 0 ? (
       <Stack>
        <Alert severity="warning">
         <Typography variant="subtitle2">
          <FormattedMessage id="cluster.noNodeFound" />
         </Typography>
        </Alert>
       </Stack>
      ) : (
       <Stack spacing={1}>
        {clusterList?.map((element, index) => {
         return (
          <ClusterServer
           key={`node-list-${index}`}
           serverData={element}
           nfsInstalled={nfsInstalled}
          />
         );
        })}
       </Stack>
      )}
      <Stack spacing={2}>
       <AddServerToCluster server_id={Number(params.id)} />
       <JoinCluster server_id={Number(params.id)} />
      </Stack>
     </Stack>
    </Stack>
   </Grid>
   <Grid xs={desktopViewPort ? 8 : 12}>
    <Stack spacing={2}>
     <ClusterServerStatistics servers={clusterList || []} />
     <ClusterProxy serviceList={serviceList} />
     <ClusterServerAppServices id={Number(params.id)} serviceList={serviceList} />
     <ClusterServerReplicas
      id={Number(params.id)}
      reloadData={reloadReplicas}
      onReload={() => setReloadReplicas(false)}
     />
    </Stack>
   </Grid>
  </Grid>
 );
};

export default ClusterManage;
